import { FC, PropsWithChildren, useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { useTranslation } from '@frontend/i18n';
import { ContentLoader } from '@frontend/design-system';
import { useFeaturePromotionModal } from '../hooks/useFeaturePromotionModal';
import { useFeaturePromotionPage } from '../hooks/useFeaturePromotionPage';
import { useFeatureSubscriptionConfig } from '../hooks/useFeatureSubscriptionConfig';
import { useFeatureUpgradeDetailsModal } from '../hooks/useFeatureUpgradeDetailsModal';
import { FeatureSubscriptionContext } from '../providers/FeatureSubscriptionProvider';
import { FeatureMultiLocationUpgradeAlert } from './FeatureMultiLocationUpgradeAlert';

export type FeatureSubscriptionGuardProps = {
  feature: Feature;
  locationIds?: string[];
  showMultiSubscriptionAlert?: boolean;
  isDemoAvailable?: boolean;
  onViewDemoClick?: () => void;
  /**
   * When true, grants access and renders children even if the main feature is inactive.
   * However, upgrade alerts and modals are still shown to encourage feature adoption.
   */
  overrideAccess?: boolean;
} & (
  | {
      allowPromoteState?: boolean;
      activeStates?: never;
    }
  | {
      allowPromoteState?: never;
      activeStates?: State[];
    }
);

const STANDARD_ACTIVE_STATES = [State.ACTIVE];
const EXTENDED_ACTIVE_STATES = [State.ACTIVE, State.PROMOTE];

export const FeatureSubscriptionGuard: FC<PropsWithChildren<FeatureSubscriptionGuardProps>> = ({
  children,
  feature,
  showMultiSubscriptionAlert = true,
  locationIds,
  isDemoAvailable = false,
  onViewDemoClick,
  allowPromoteState,
  activeStates = STANDARD_ACTIVE_STATES,
  overrideAccess = false,
}) => {
  const featureSubscriptionConfig = useFeatureSubscriptionConfig({
    feature,
    locationIds,
    activeStates: allowPromoteState ? EXTENDED_ACTIVE_STATES : activeStates,
  });
  const { isBundleUpgradeEligible, isFeatureActiveInAllLocations, isFeatureHiddenInAllLocations, isLoading } =
    featureSubscriptionConfig;
  const { featurePromotionModalControls, FeaturePromotionModal } = useFeaturePromotionModal();
  const { FeatureUpgradeDetailsModal, featureUpgradeDetailsModalControls } = useFeatureUpgradeDetailsModal({
    feature,
    isBundleUpgradeEligible,
  });
  const { FeaturePromotionPage } = useFeaturePromotionPage(feature);

  const { t } = useTranslation('featurePromotion', { keyPrefix: 'feature-subscription-guard' });

  const { shouldShowChildren, shouldShowPromotionPage, shouldShowMultiLocationAlert } = useMemo(
    () => ({
      // Show children when feature is active or override access is granted
      shouldShowChildren: (overrideAccess || !isFeatureHiddenInAllLocations) && !isLoading,
      // Show teaser if feature is hidden in all of the location and promotion page is available
      shouldShowPromotionPage: !overrideAccess && isFeatureHiddenInAllLocations,
      // Show upgrade alert when not fully active and either partially active or has override access
      shouldShowMultiLocationAlert:
        showMultiSubscriptionAlert && !isFeatureActiveInAllLocations && !isFeatureHiddenInAllLocations,
    }),
    [
      overrideAccess,
      isFeatureHiddenInAllLocations,
      isLoading,
      showMultiSubscriptionAlert,
      isFeatureActiveInAllLocations,
    ]
  );

  return (
    <FeatureSubscriptionContext.Provider value={featureSubscriptionConfig}>
      <ContentLoader show={isLoading} message={t('Checking Subscription')} />
      {/* Show children if feature is Active in some of the location */}
      {shouldShowChildren && children}
      {/* Show teaser if feature is hidden in all of the location and promotion page is available*/}
      {shouldShowPromotionPage && FeaturePromotionPage && (
        <FeaturePromotionPage
          mode='fullPage'
          handleOpenUpgradeModal={featureUpgradeDetailsModalControls.openModal}
          isDemoAvailable={isDemoAvailable}
          handleViewDemo={onViewDemoClick}
        />
      )}
      {/* Show multi location alert if feature is active in some of the locations */}
      {shouldShowMultiLocationAlert && (
        <FeatureMultiLocationUpgradeAlert
          feature={feature}
          onUpgradeActionClick={
            !!FeatureUpgradeDetailsModal
              ? featureUpgradeDetailsModalControls.openModal
              : featurePromotionModalControls.openModal
          }
        />
      )}
      <FeaturePromotionModal
        feature={feature}
        modalControls={featurePromotionModalControls}
        handleOpenUpgradeModal={featureUpgradeDetailsModalControls.openModal}
      />
      {!!FeatureUpgradeDetailsModal && (
        <FeatureUpgradeDetailsModal
          modalControls={featureUpgradeDetailsModalControls}
          onActionSuccess={() => {
            featureUpgradeDetailsModalControls.closeModal();
          }}
        />
      )}
    </FeatureSubscriptionContext.Provider>
  );
};
